"use client"
import { useState, useEffect } from "react"
import "../styles/calculadorTasas.css"
import { doc, setDoc, getDoc, addDoc, collection } from "firebase/firestore"
import { db } from "../firebaseconfig"
import { useAuth } from "../context/AuthContext"
import { generarSimuladorPDF } from "../components/reports/PDFCreditoSimulador"
import logo from "../assets/logo2.png"
import { Car, Bike, Settings, Save, FileText, CreditCard, AlertCircle, CheckCircle, Calculator } from "lucide-react"
import InfoNotice from "../components/simulator/InfoNotice"

const CalculadorTasas = () => {
  const { user } = useAuth()
  const [userData, setUserData] = useState(null)

  const mesActual = new Date().toLocaleString("es-AR", { month: "long" })
  const añoActual = new Date().getFullYear()

  useEffect(() => {
    const loadUserData = async () => {
      try {
        const userRef = doc(db, "users", user.uid)
        const userSnap = await getDoc(userRef)
        if (userSnap.exists()) setUserData(userSnap.data())
        else console.log("No se encontró el usuario en Firestore")
      } catch (error) {
        console.error("Error al cargar datos del usuario:", error)
      }
    }
    if (user?.uid) loadUserData()
  }, [user?.uid])

  const [showConfig, setShowConfig] = useState(false)
  const [configSaved, setConfigSaved] = useState(false)
  const [activeConfigTab, setActiveConfigTab] = useState("autoDentro10Anos")

  const [vehicleData, setVehicleData] = useState({
    marca: "",
    modelo: "",
    año: new Date().getFullYear(),
    version: "",
    precio: 0,
    tipoVehiculo: "auto",
  })

  const [configsAutoDentro10Anos, setConfigsAutoDentro10Anos] = useState({
    prendario: {
      tasaAnual: 35,
      cuotasMaximas: 60,
      porcentajeMaximo: 80,
      minAmount: 500000,
      maxAmount: 7000000,
      minTerm: 6,
      maxTerm: 60,
    },
  })

  const [configsAutoMenos10Anos, setConfigsAutoMenos10Anos] = useState({
    prendario: {
      tasaAnual: 35,
      cuotasMaximas: 60,
      porcentajeMaximo: 80,
      minAmount: 500000,
      maxAmount: 7000000,
      minTerm: 6,
      maxTerm: 60,
    },
  })

  const [configsMoto, setConfigsMoto] = useState({
    prendario: {
      tasaAnual: 35,
      cuotasMaximas: 60,
      porcentajeMaximo: 80,
      minAmount: 500000,
      maxAmount: 7000000,
      minTerm: 6,
      maxTerm: 60,
    },
  })

  useEffect(() => {
    const loadConfigFromFirebase = async () => {
      try {
        const docRefAutoDentro = doc(db, "configuraciones", "simuladorAutoDentro10Anos")
        const docSnapAutoDentro = await getDoc(docRefAutoDentro)
        if (docSnapAutoDentro.exists()) {
          setConfigsAutoDentro10Anos(docSnapAutoDentro.data())
        } else {
          await setDoc(docRefAutoDentro, configsAutoDentro10Anos)
        }

        const docRefAutoMenos = doc(db, "configuraciones", "simuladorAutoMenos10Anos")
        const docSnapAutoMenos = await getDoc(docRefAutoMenos)
        if (docSnapAutoMenos.exists()) {
          setConfigsAutoMenos10Anos(docSnapAutoMenos.data())
        } else {
          await setDoc(docRefAutoMenos, configsAutoMenos10Anos)
        }

        const docRefMoto = doc(db, "configuraciones", "simuladorMoto")
        const docSnapMoto = await getDoc(docRefMoto)
        if (docSnapMoto.exists()) {
          setConfigsMoto(docSnapMoto.data())
        } else {
          await setDoc(docRefMoto, configsMoto)
        }
      } catch (error) {
        console.error("Error al cargar configuraciones:", error)
      }
    }
    loadConfigFromFirebase()
  }, [])

  const [loanData, setLoanData] = useState({
    amount: 0,
    term: 18,
    vehicleValue: 0,
  })

  const [results, setResults] = useState({})
  const [clientData, setClientData] = useState({
    nombre: "",
    apellido: "",
    dni: "",
    telefono: "",
    email: "",
  })

  const [showClientForm, setShowClientForm] = useState(false)

  const getActiveConfig = () => {
    if (vehicleData.tipoVehiculo === "moto") return configsMoto
    const vehicleAge = new Date().getFullYear() - vehicleData.año
    return vehicleAge <= 10 ? configsAutoDentro10Anos : configsAutoMenos10Anos
  }

  const configs = getActiveConfig()

  useEffect(() => {
    setLoanData((prev) => ({ ...prev, vehicleValue: vehicleData.precio }))
  }, [vehicleData.precio])

  useEffect(() => {
    if (loanData.vehicleValue > 0) {
      const maxAmount = Math.round(loanData.vehicleValue * (configs.prendario.porcentajeMaximo / 100))
      if (loanData.amount > maxAmount) {
        setLoanData((prev) => ({ ...prev, amount: maxAmount }))
      }
    }
  }, [loanData.vehicleValue, configs.prendario.porcentajeMaximo])

  const calculateLoan = (amount, annualRate, termMonths) => {
    const monthlyRate = annualRate / 100 / 12
    const monthlyPayment =
      (amount * (monthlyRate * Math.pow(1 + monthlyRate, termMonths))) /
      (Math.pow(1 + monthlyRate, termMonths) - 1)
    const totalAmount = monthlyPayment * termMonths
    const totalInterest = totalAmount - amount
    return { monthlyPayment, totalInterest, totalAmount }
  }

  useEffect(() => {
    if (loanData.amount > 0 && loanData.term > 0) {
      const result = calculateLoan(loanData.amount, configs.prendario.tasaAnual, loanData.term)
      setResults(result)
    }
  }, [loanData, configs.prendario.tasaAnual])

  const updateLoanData = (field, value) => {
    setLoanData((prev) => ({ ...prev, [field]: value }))
  }

  const updateConfig = (configType, field, value) => {
    setConfigSaved(false)
    const updater = (prev) => ({ ...prev, prendario: { ...prev.prendario, [field]: value } })
    if (configType === "autoDentro10Anos") setConfigsAutoDentro10Anos(updater)
    else if (configType === "autoMenos10Anos") setConfigsAutoMenos10Anos(updater)
    else if (configType === "moto") setConfigsMoto(updater)
  }

  const updateVehicleData = (field, value) => {
    setVehicleData((prev) => ({ ...prev, [field]: value }))
  }

  const saveConfiguration = async () => {
    try {
      await setDoc(doc(db, "configuraciones", "simuladorAutoDentro10Anos"), configsAutoDentro10Anos)
      await setDoc(doc(db, "configuraciones", "simuladorAutoMenos10Anos"), configsAutoMenos10Anos)
      await setDoc(doc(db, "configuraciones", "simuladorMoto"), configsMoto)
      setConfigSaved(true)
      setTimeout(() => setConfigSaved(false), 3000)
    } catch (error) {
      console.error("Error al guardar configuración:", error)
      alert("Error al guardar la configuración. Intente nuevamente.")
    }
  }

  const formatCurrency = (amount) =>
    new Intl.NumberFormat("es-AR", {
      style: "currency",
      currency: "ARS",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount)

  const validarDatosVehiculo = () => {
    const errores = []
    if (!vehicleData.marca.trim()) errores.push("Marca del vehículo")
    if (!vehicleData.modelo.trim()) errores.push("Modelo del vehículo")
    if (!vehicleData.año || vehicleData.año < 1990) errores.push("Año del vehículo")
    if (!vehicleData.version.trim()) errores.push("Versión del vehículo")
    if (loanData.vehicleValue <= 0) errores.push("Precio del vehículo")
    if (loanData.amount <= 0) errores.push("Monto del préstamo")
    if (loanData.term <= 0) errores.push("Plazo del préstamo")
    return errores
  }

  const validarDatosCliente = () => {
    const errores = []
    if (!clientData.nombre.trim()) errores.push("Nombre")
    if (!clientData.apellido.trim()) errores.push("Apellido")
    if (!clientData.dni.trim()) errores.push("DNI")
    if (!clientData.telefono.trim()) errores.push("Teléfono")
    if (!clientData.email.trim()) errores.push("Email")
    return errores
  }

  const getMaxLoanAmount = () => {
    if (loanData.vehicleValue > 0) {
      return Math.round(loanData.vehicleValue * (configs.prendario.porcentajeMaximo / 100))
    }
    return configs.prendario.maxAmount
  }

  const getSliderBackground = (value, min, max) => {
    const percent = ((value - min) / (max - min)) * 100
    return `linear-gradient(to right, #003226 0%, #003226 ${percent}%, #DBC5A8 ${percent}%, #DBC5A8 100%)`
  }

  const generarPDFyWhatsApp = async () => {
    const erroresVehiculo = validarDatosVehiculo()
    if (erroresVehiculo.length > 0) {
      alert(`Por favor complete los siguientes datos:\n\n• ${erroresVehiculo.join("\n• ")}`)
      return
    }
    if (!results.monthlyPayment) {
      alert("Error en el cálculo del préstamo. Verifique todos los datos.")
      return
    }
    try {
      const nombreArchivo = generarSimuladorPDF(vehicleData, loanData, results, configs, { logo })
      const mensaje =
        `*CRÉDITO PRENDARIO - SIMULACIÓN*\n\n` +
        `*DATOS DEL VEHÍCULO*\n` +
        `• Marca: ${vehicleData.marca}\n` +
        `• Modelo: ${vehicleData.modelo}\n` +
        `• Año: ${vehicleData.año}\n` +
        `• Versión: ${vehicleData.version}\n` +
        `• Valor: ${formatCurrency(loanData.vehicleValue)}\n\n` +
        `*DATOS DEL PRÉSTAMO*\n` +
        `• Monto solicitado: ${formatCurrency(loanData.amount)}\n` +
        `• Plazo: ${loanData.term} meses\n` +
        `• Cuota mensual: ${formatCurrency(results.monthlyPayment)}\n\n` +
        `_Simulación generada por Financiera Gamaan_`
      setTimeout(() => {
        const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(mensaje)}`
        window.open(whatsappUrl, "_blank")
        alert(
          `PDF generado: ${nombreArchivo}\n\nWhatsApp se abrirá para que puedas adjuntar el archivo y enviarlo.`
        )
      }, 500)
    } catch (error) {
      console.error("Error al generar PDF:", error)
      alert("Error al generar el PDF. Intente nuevamente.")
    }
  }

  const solicitarCredito = () => {
    const erroresVehiculo = validarDatosVehiculo()
    if (erroresVehiculo.length > 0) {
      alert(`Por favor complete los siguientes datos:\n\n• ${erroresVehiculo.join("\n• ")}`)
      return
    }
    if (!results.monthlyPayment) {
      alert("Error en el cálculo del préstamo. Verifique todos los datos.")
      return
    }
    setShowClientForm(true)
  }

  const enviarSolicitudCredito = async () => {
    const erroresCliente = validarDatosCliente()
    if (erroresCliente.length > 0) {
      alert(`Por favor complete los siguientes datos:\n\n• ${erroresCliente.join("\n• ")}`)
      return
    }
    const erroresVehiculo = validarDatosVehiculo()
    if (erroresVehiculo.length > 0) {
      alert(`Error en los datos del vehículo:\n\n• ${erroresVehiculo.join("\n• ")}`)
      return
    }

    try {
      const solicitudCompleta = {
        nombre: `${clientData.nombre} ${clientData.apellido}`,
        dni: clientData.dni,
        email: clientData.email || "",
        telefono: clientData.telefono,
        ingresos: 0,
        montoSolicitado: loanData.amount,
        plazo: loanData.term,
        tipoVehiculo: vehicleData.tipoVehiculo === "moto" ? "Moto" : "Automóvil",
        marca: vehicleData.marca,
        modelo: vehicleData.modelo,
        año: vehicleData.año,
        version: vehicleData.version,
        valorVehiculo: loanData.vehicleValue,
        cuotaCalculada: results.monthlyPayment,
        totalAPagar: results.totalAmount,
        tasaAnual: configs.prendario.tasaAnual,
        agenciaUsuario: userData
          ? {
              id: user.uid,
              nombre: `${userData.firstName || ""} ${userData.lastName || ""}`.trim(),
              email: userData.email || user.email,
              rol: userData.rol || "usuario",
              esAgencia: userData.rol === "agencia",
            }
          : null,
        estado: "pendiente",
        fechaCreacion: new Date().toISOString(),
        observaciones: `Solicitud generada desde simulador por: ${
          userData
            ? `${userData.firstName || ""} ${userData.lastName || ""}`.trim() || userData.email
            : "usuario no identificado"
        }`,
        origen: "simulador",
        tipoSimulacion: "prendario",
      }

      const docRef = await addDoc(collection(db, "creditosPrendarios"), solicitudCompleta)
      alert(
        `Solicitud enviada correctamente.\n\nID: ${docRef.id.substring(0, 8)}...\n\nPodrás ver el estado en el panel administrativo.`
      )
      setShowClientForm(false)
      setClientData({ nombre: "", apellido: "", dni: "", telefono: "", email: "" })
      setVehicleData({
        marca: "",
        modelo: "",
        año: new Date().getFullYear(),
        version: "",
        precio: 0,
        tipoVehiculo: "auto",
      })
      setLoanData({ amount: 0, term: 18, vehicleValue: 0 })
      setResults({})
    } catch (error) {
      console.error("Error enviando solicitud:", error)
      alert("Error al enviar la solicitud. Intente nuevamente.")
    }
  }

  const isAdmin = userData?.rol === "admin"

  const tabStyle = (active) => ({
    padding: "12px 20px",
    border: "none",
    borderBottom: active ? "3px solid #003226" : "3px solid transparent",
    background: "transparent",
    cursor: "pointer",
    fontWeight: active ? "bold" : "normal",
    color: active ? "#003226" : "#666",
    display: "flex",
    alignItems: "center",
    gap: "6px",
  })

  const vehicleAgeLabel = () => {
    if (vehicleData.tipoVehiculo === "moto") return "Motos"
    const age = new Date().getFullYear() - vehicleData.año
    return age <= 10 ? "Autos hasta 10 años" : "Autos de más de 10 años"
  }

  return (
    <div className="loan-calculator">
      {/* Admin config toggle */}
      {userData && isAdmin && (
        <div className="admin-header">
          <div className="admin-info">
            <button
              className={`config-button ${showConfig ? "active" : ""}`}
              onClick={() => setShowConfig(!showConfig)}
            >
              <Settings size={16} />
              Configuración
            </button>
          </div>
        </div>
      )}

      {/* Panel de configuración */}
      {showConfig && userData && isAdmin && (
        <div className="config-panel">
          <div className="config-header">
            <h2>Configuración del Simulador</h2>
            <p>Ajustá los parámetros del crédito prendario por tipo de vehículo</p>
          </div>

          <div
            className="config-tabs"
            style={{ display: "flex", marginBottom: "20px", borderBottom: "2px solid #e0e0e0" }}
          >
            <button
              style={tabStyle(activeConfigTab === "autoDentro10Anos")}
              onClick={() => setActiveConfigTab("autoDentro10Anos")}
            >
              <Car size={16} /> Autos hasta 10 años
            </button>
            <button
              style={tabStyle(activeConfigTab === "autoMenos10Anos")}
              onClick={() => setActiveConfigTab("autoMenos10Anos")}
            >
              <Car size={16} /> Autos +10 años
            </button>
            <button
              style={tabStyle(activeConfigTab === "moto")}
              onClick={() => setActiveConfigTab("moto")}
            >
              <Bike size={16} /> Motos
            </button>
          </div>

          <div className="config-content">
            <div className="config-grid">
              {activeConfigTab === "autoDentro10Anos" && (
                <div className="config-section">
                  <h3 style={{ display: "flex", alignItems: "center", gap: "8px", justifyContent: "center" }}>
                    <Car size={20} /> Autos hasta 10 años
                  </h3>
                  <div className="config-inputs">
                    <div className="input-group">
                      <label>% Anual</label>
                      <input
                        type="number"
                        step="0.1"
                        value={configsAutoDentro10Anos.prendario.tasaAnual}
                        onChange={(e) =>
                          updateConfig("autoDentro10Anos", "tasaAnual", Number.parseFloat(e.target.value))
                        }
                      />
                    </div>
                    <div className="input-group">
                      <label>Cuotas Máximas</label>
                      <input
                        type="number"
                        value={configsAutoDentro10Anos.prendario.cuotasMaximas}
                        onChange={(e) =>
                          updateConfig("autoDentro10Anos", "cuotasMaximas", Number.parseInt(e.target.value))
                        }
                      />
                    </div>
                    <div className="input-group">
                      <label>% Máximo del Valor del Vehículo</label>
                      <input
                        type="number"
                        value={configsAutoDentro10Anos.prendario.porcentajeMaximo}
                        onChange={(e) =>
                          updateConfig("autoDentro10Anos", "porcentajeMaximo", Number.parseInt(e.target.value))
                        }
                      />
                    </div>
                    <div className="input-group">
                      <label>Monto Mínimo</label>
                      <input
                        type="number"
                        value={configsAutoDentro10Anos.prendario.minAmount}
                        onChange={(e) =>
                          updateConfig("autoDentro10Anos", "minAmount", Number.parseInt(e.target.value))
                        }
                      />
                    </div>
                    <div className="input-group">
                      <label>Plazo Mínimo (meses)</label>
                      <input
                        type="number"
                        value={configsAutoDentro10Anos.prendario.minTerm}
                        onChange={(e) =>
                          updateConfig("autoDentro10Anos", "minTerm", Number.parseInt(e.target.value))
                        }
                      />
                    </div>
                  </div>
                </div>
              )}

              {activeConfigTab === "autoMenos10Anos" && (
                <div className="config-section">
                  <h3 style={{ display: "flex", alignItems: "center", gap: "8px", justifyContent: "center" }}>
                    <Car size={20} /> Autos de más de 10 años
                  </h3>
                  <div className="config-inputs">
                    <div className="input-group">
                      <label>% Anual</label>
                      <input
                        type="number"
                        step="0.1"
                        value={configsAutoMenos10Anos.prendario.tasaAnual}
                        onChange={(e) =>
                          updateConfig("autoMenos10Anos", "tasaAnual", Number.parseFloat(e.target.value))
                        }
                      />
                    </div>
                    <div className="input-group">
                      <label>Cuotas Máximas</label>
                      <input
                        type="number"
                        value={configsAutoMenos10Anos.prendario.cuotasMaximas}
                        onChange={(e) =>
                          updateConfig("autoMenos10Anos", "cuotasMaximas", Number.parseInt(e.target.value))
                        }
                      />
                    </div>
                    <div className="input-group">
                      <label>% Máximo del Valor del Vehículo</label>
                      <input
                        type="number"
                        value={configsAutoMenos10Anos.prendario.porcentajeMaximo}
                        onChange={(e) =>
                          updateConfig("autoMenos10Anos", "porcentajeMaximo", Number.parseInt(e.target.value))
                        }
                      />
                    </div>
                    <div className="input-group">
                      <label>Monto Mínimo</label>
                      <input
                        type="number"
                        value={configsAutoMenos10Anos.prendario.minAmount}
                        onChange={(e) =>
                          updateConfig("autoMenos10Anos", "minAmount", Number.parseInt(e.target.value))
                        }
                      />
                    </div>
                    <div className="input-group">
                      <label>Plazo Mínimo (meses)</label>
                      <input
                        type="number"
                        value={configsAutoMenos10Anos.prendario.minTerm}
                        onChange={(e) =>
                          updateConfig("autoMenos10Anos", "minTerm", Number.parseInt(e.target.value))
                        }
                      />
                    </div>
                  </div>
                </div>
              )}

              {activeConfigTab === "moto" && (
                <div className="config-section">
                  <h3 style={{ display: "flex", alignItems: "center", gap: "8px", justifyContent: "center" }}>
                    <Bike size={20} /> Motos
                  </h3>
                  <div className="config-inputs">
                    <div className="input-group">
                      <label>% Anual</label>
                      <input
                        type="number"
                        step="0.1"
                        value={configsMoto.prendario.tasaAnual}
                        onChange={(e) => updateConfig("moto", "tasaAnual", Number.parseFloat(e.target.value))}
                      />
                    </div>
                    <div className="input-group">
                      <label>Cuotas Máximas</label>
                      <input
                        type="number"
                        value={configsMoto.prendario.cuotasMaximas}
                        onChange={(e) => updateConfig("moto", "cuotasMaximas", Number.parseInt(e.target.value))}
                      />
                    </div>
                    <div className="input-group">
                      <label>% Máximo del Valor del Vehículo</label>
                      <input
                        type="number"
                        value={configsMoto.prendario.porcentajeMaximo}
                        onChange={(e) => updateConfig("moto", "porcentajeMaximo", Number.parseInt(e.target.value))}
                      />
                    </div>
                    <div className="input-group">
                      <label>Monto Mínimo</label>
                      <input
                        type="number"
                        value={configsMoto.prendario.minAmount}
                        onChange={(e) => updateConfig("moto", "minAmount", Number.parseInt(e.target.value))}
                      />
                    </div>
                    <div className="input-group">
                      <label>Plazo Mínimo (meses)</label>
                      <input
                        type="number"
                        value={configsMoto.prendario.minTerm}
                        onChange={(e) => updateConfig("moto", "minTerm", Number.parseInt(e.target.value))}
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
            <div className="config-actions">
              <button
                className={`save-button ${configSaved ? "saved" : ""}`}
                onClick={saveConfiguration}
              >
                {configSaved ? (
                  <>
                    <CheckCircle size={16} />
                    Guardado
                  </>
                ) : (
                  <>
                    <Save size={16} />
                    Guardar Configuración
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Datos del Vehículo */}
      <div className="simulator-container" style={{ marginBottom: "20px" }}>
        <div className="simulator-header">
          <div className="simulator-icon">
            <Car size={32} />
          </div>
          <div>
            <h1>Datos del Vehículo</h1>
            <p style={{ color: "#DBC5A8" }}>Ingresá los datos y el precio de referencia del vehículo</p>
          </div>
        </div>

        <div className="simulator-controls">
          {/* Selector de tipo de vehículo */}
          <div
            style={{
              marginBottom: "20px",
              padding: "15px",
              backgroundColor: "#f8f9fa",
              borderRadius: "8px",
              border: "2px solid #e0e0e0",
            }}
          >
            <label
              style={{ fontSize: "16px", fontWeight: "bold", marginBottom: "10px", display: "block", color: "#333" }}
            >
              Tipo de Vehículo *
            </label>
            <div style={{ display: "flex", gap: "20px" }}>
              <label
                style={{ display: "flex", alignItems: "center", gap: "8px", cursor: "pointer", fontSize: "16px" }}
              >
                <input
                  type="radio"
                  name="tipoVehiculo"
                  value="auto"
                  checked={vehicleData.tipoVehiculo === "auto"}
                  onChange={(e) => updateVehicleData("tipoVehiculo", e.target.value)}
                  style={{ transform: "scale(1.2)" }}
                />
                <Car size={18} />
                Automóvil
              </label>
              <label
                style={{ display: "flex", alignItems: "center", gap: "8px", cursor: "pointer", fontSize: "16px" }}
              >
                <input
                  type="radio"
                  name="tipoVehiculo"
                  value="moto"
                  checked={vehicleData.tipoVehiculo === "moto"}
                  onChange={(e) => updateVehicleData("tipoVehiculo", e.target.value)}
                  style={{ transform: "scale(1.2)" }}
                />
                <Bike size={18} />
                Moto
              </label>
            </div>

            <div
              style={{
                marginTop: "10px",
                padding: "10px",
                backgroundColor: "#e8f5e8",
                borderRadius: "6px",
                fontSize: "14px",
              }}
            >
              <strong>Configuración aplicada:</strong> {vehicleAgeLabel()}
              {vehicleData.año && vehicleData.tipoVehiculo === "auto" && (
                <span style={{ marginLeft: "10px", color: "#666" }}>
                  (Antigüedad: {new Date().getFullYear() - vehicleData.año} años)
                </span>
              )}
            </div>
          </div>

          {/* Campos del vehículo */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
              gap: "20px",
              marginBottom: "20px",
            }}
          >
            <div className="input-group">
              <label>Marca *</label>
              <input
                type="text"
                value={vehicleData.marca}
                onChange={(e) => updateVehicleData("marca", e.target.value)}
                placeholder="Ej: Toyota"
                style={{ padding: "12px", border: "2px solid #e0e0e0", borderRadius: "8px", fontSize: "16px" }}
                required
              />
            </div>
            <div className="input-group">
              <label>Modelo *</label>
              <input
                type="text"
                value={vehicleData.modelo}
                onChange={(e) => updateVehicleData("modelo", e.target.value)}
                placeholder="Ej: Corolla"
                style={{ padding: "12px", border: "2px solid #e0e0e0", borderRadius: "8px", fontSize: "16px" }}
                required
              />
            </div>
            <div className="input-group">
              <label>Año *</label>
              <input
                type="number"
                min="1990"
                max={new Date().getFullYear() + 1}
                value={vehicleData.año}
                onChange={(e) => updateVehicleData("año", Number.parseInt(e.target.value))}
                style={{ padding: "12px", border: "2px solid #e0e0e0", borderRadius: "8px", fontSize: "16px" }}
                required
              />
            </div>
            <div className="input-group">
              <label>Versión *</label>
              <input
                type="text"
                value={vehicleData.version}
                onChange={(e) => updateVehicleData("version", e.target.value)}
                placeholder="Ej: XEI 1.8"
                style={{ padding: "12px", border: "2px solid #e0e0e0", borderRadius: "8px", fontSize: "16px" }}
                required
              />
            </div>
          </div>

          {/* Precio del vehículo */}
          <div className="input-group" style={{ marginBottom: "4px" }}>
            <label style={{ fontWeight: "bold", color: "#333", fontSize: "15px" }}>
              Precio del Vehículo (ARS) *
            </label>
            <input
              type="number"
              value={vehicleData.precio || ""}
              onChange={(e) => updateVehicleData("precio", Number.parseInt(e.target.value) || 0)}
              placeholder="Ingresá el precio de referencia en pesos"
              style={{
                padding: "12px",
                border: vehicleData.precio > 0 ? "2px solid #003226" : "2px solid #e0e0e0",
                borderRadius: "8px",
                fontSize: "16px",
                width: "100%",
                transition: "border-color 0.2s",
              }}
              required
            />
          </div>

          <InfoNotice mesActual={mesActual} añoActual={añoActual} />
        </div>
      </div>

      {/* Simulador de Crédito */}
      <div className="simulator-container">
        <div className="simulator-header">
          <div className="simulator-icon">
            <Calculator size={32} />
          </div>
          <div>
            <h1>Simulador de Crédito Prendario</h1>
            <p style={{ color: "#DBC5A8" }}>Calculá el monto de tus cuotas mensuales</p>
          </div>
        </div>

        <div className="simulator-controls">
          {/* Monto del préstamo */}
          <div className="control-group">
            <div className="control-header">
              <label>Monto del préstamo</label>
              <span className="control-value">{formatCurrency(loanData.amount)}</span>
            </div>
            <div className="slider-container">
              <input
                type="range"
                min={configs.prendario.minAmount}
                max={getMaxLoanAmount()}
                step="50000"
                value={loanData.amount}
                onChange={(e) => updateLoanData("amount", Number.parseInt(e.target.value))}
                className="slider amount-slider"
                style={{
                  background: getSliderBackground(loanData.amount, configs.prendario.minAmount, getMaxLoanAmount()),
                }}
              />
              <div className="slider-labels">
                <span>{formatCurrency(configs.prendario.minAmount)}</span>
                <span>{formatCurrency(getMaxLoanAmount())}</span>
              </div>
            </div>
            <div style={{ fontSize: "12px", color: "#666", marginTop: "5px" }}>
              Máximo financiable: {configs.prendario.porcentajeMaximo}% del valor del vehículo
            </div>
          </div>

          {/* Plazo en meses */}
          <div className="control-group">
            <div className="control-header">
              <label>Plazo en meses</label>
              <span className="control-value">{loanData.term} meses</span>
            </div>
            <div className="slider-container">
              <input
                type="range"
                min={configs.prendario.minTerm}
                max={configs.prendario.cuotasMaximas}
                step="1"
                value={loanData.term}
                onChange={(e) => updateLoanData("term", Number.parseInt(e.target.value))}
                className="slider term-slider"
                style={{
                  background: getSliderBackground(
                    loanData.term,
                    configs.prendario.minTerm,
                    configs.prendario.cuotasMaximas
                  ),
                }}
              />
              <div className="slider-labels">
                <span>{configs.prendario.minTerm} meses</span>
                <span>{configs.prendario.cuotasMaximas} meses</span>
              </div>
            </div>
          </div>
        </div>

        {/* Resultados */}
        {results.monthlyPayment && (
          <div className="results-section">
            <div className="result-cards">
              <div className="result-card">
                <div className="result-label">Cuota mensual</div>
                <div className="result-amount">{formatCurrency(results.monthlyPayment)}</div>
              </div>
              <div className="result-card">
                <div className="result-label">Tasa anual</div>
                <div className="result-amount">{configs.prendario.tasaAnual}%</div>
              </div>
            </div>

            {loanData.vehicleValue <= 0 && (
              <div
                style={{
                  marginTop: "15px",
                  padding: "12px 16px",
                  backgroundColor: "#fff8f0",
                  border: "1px solid #DBC5A8",
                  borderLeft: "4px solid #003226",
                  borderRadius: "8px",
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                }}
              >
                <AlertCircle size={16} color="#003226" />
                <p style={{ margin: 0, fontSize: "14px", color: "#444" }}>
                  <strong>Completá el precio del vehículo</strong> para poder generar el PDF o solicitar el crédito.
                </p>
              </div>
            )}

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "15px", marginTop: "20px" }}>
              <button
                className="solicitar-button"
                onClick={generarPDFyWhatsApp}
                style={{
                  background: "#25D366",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "8px",
                }}
              >
                <FileText size={18} />
                Generar PDF y WhatsApp
              </button>
              <button
                className="solicitar-button"
                onClick={solicitarCredito}
                style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "8px" }}
              >
                <CreditCard size={18} />
                Solicitar Crédito
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Modal datos del cliente */}
      {showClientForm && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0,0,0,0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
          }}
        >
          <div
            style={{
              background: "white",
              padding: "30px",
              borderRadius: "15px",
              width: "90%",
              maxWidth: "500px",
              maxHeight: "90vh",
              overflow: "auto",
            }}
          >
            <h2 style={{ marginTop: 0, color: "#003226" }}>Datos del Cliente</h2>

            {userData && (
              <div
                style={{
                  background: "#f0f8f4",
                  padding: "15px",
                  borderRadius: "8px",
                  marginBottom: "20px",
                  border: "1px solid #1a5f3f",
                }}
              >
                <h4 style={{ margin: "0 0 10px 0", color: "#1a5f3f" }}>
                  {userData.rol === "agencia" ? "Cargado por Agencia:" : "Cargado por:"}
                </h4>
                <p style={{ margin: "5px 0", fontSize: "14px" }}>
                  <strong>Nombre:</strong>{" "}
                  {`${userData.firstName || ""} ${userData.lastName || ""}`.trim() || "No disponible"}
                </p>
                <p style={{ margin: "5px 0", fontSize: "14px" }}>
                  <strong>Email:</strong> {userData.email || user?.email || "No disponible"}
                </p>
                <p style={{ margin: "5px 0", fontSize: "14px" }}>
                  <strong>Rol:</strong> {userData.rol || "Usuario"}
                </p>
              </div>
            )}

            <div style={{ display: "grid", gap: "15px" }}>
              <div className="input-group">
                <label>Nombre *</label>
                <input
                  type="text"
                  value={clientData.nombre}
                  onChange={(e) => setClientData((prev) => ({ ...prev, nombre: e.target.value }))}
                  style={{ padding: "12px", border: "2px solid #e0e0e0", borderRadius: "8px", fontSize: "16px" }}
                />
              </div>
              <div className="input-group">
                <label>Apellido *</label>
                <input
                  type="text"
                  value={clientData.apellido}
                  onChange={(e) => setClientData((prev) => ({ ...prev, apellido: e.target.value }))}
                  style={{ padding: "12px", border: "2px solid #e0e0e0", borderRadius: "8px", fontSize: "16px" }}
                />
              </div>
              <div className="input-group">
                <label>DNI *</label>
                <input
                  type="text"
                  value={clientData.dni}
                  onChange={(e) => setClientData((prev) => ({ ...prev, dni: e.target.value }))}
                  style={{ padding: "12px", border: "2px solid #e0e0e0", borderRadius: "8px", fontSize: "16px" }}
                />
              </div>
              <div className="input-group">
                <label>Teléfono *</label>
                <input
                  type="tel"
                  value={clientData.telefono}
                  onChange={(e) => setClientData((prev) => ({ ...prev, telefono: e.target.value }))}
                  style={{ padding: "12px", border: "2px solid #e0e0e0", borderRadius: "8px", fontSize: "16px" }}
                />
              </div>
              <div className="input-group">
                <label>Email *</label>
                <input
                  type="email"
                  value={clientData.email}
                  onChange={(e) => setClientData((prev) => ({ ...prev, email: e.target.value }))}
                  style={{ padding: "12px", border: "2px solid #e0e0e0", borderRadius: "8px", fontSize: "16px" }}
                />
              </div>
            </div>

            <div style={{ display: "flex", gap: "10px", marginTop: "20px" }}>
              <button className="solicitar-button" onClick={enviarSolicitudCredito} style={{ flex: 1 }}>
                Enviar Solicitud
              </button>
              <button className="config-button" onClick={() => setShowClientForm(false)} style={{ flex: 1 }}>
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default CalculadorTasas
